// The false-positive-critical guard for the type-mismatch producer.
//
// `n = '5'` coerces cleanly on ALL five engines (empirically verified —
// Docs/TypeCheckDiagnostics.md), so a numeric column compared to a
// NUMERIC-LOOKING string literal must NEVER be flagged. Only a numeric column
// compared to a string whose content is NOT a valid number is a real bug.
// This module decides "is this quoted string a number?" — get it wrong and the
// whole feature either misses bugs or, far worse, squiggles valid SQL.

// A SQL numeric: optional sign, integer / decimal / leading-dot / scientific.
// Deliberately rejects thousands separators (`'1,000'`), hex (`'0x1F'`),
// `'NaN'`, `'Infinity'`, empty — all of which the engines treat as
// non-numeric in a numeric comparison context.
const NUMERIC_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/**
 * Strip a SQL string-literal's quotes, honouring the `N'…'` / `n'…'`
 * national-character prefix. Returns the inner content, or `null` when the
 * text is not a single-quoted string literal (the caller then treats it as
 * non-numeric — conservative).
 */
export function stripStringLiteralQuotes(literalText: string): string | null {
  let s = literalText.trim();
  if (s.length >= 1 && (s[0] === 'N' || s[0] === 'n')) {
    s = s.slice(1);
  }
  if (s.length < 2) return null;
  if (s[0] !== "'" || s[s.length - 1] !== "'") return null;
  return s.slice(1, -1);
}

/**
 * True when `literalText` is a single-quoted string whose content represents a
 * number (after trimming surrounding whitespace inside the quotes). `'5'`,
 * `'5.0'`, `'.5'`, `'1e3'`, `' -3 '`, `N'42'` → true. `'abc'`, `''`, `'5x'`,
 * `'0x1F'`, `'1,000'` → false. Non-string input → false.
 */
export function quotedStringIsNumeric(literalText: string): boolean {
  const inner = stripStringLiteralQuotes(literalText);
  if (inner === null) return false;
  const trimmed = inner.trim();
  if (trimmed === '') return false;
  return NUMERIC_RE.test(trimmed);
}

// The exact set of string contents PostgreSQL accepts as boolean input
// (case-insensitive, surrounding whitespace trimmed) — empirically confirmed
// against a live PG 16 instance. Used to keep `active = 'yes'` from being
// flagged by the boolean-vs-nonboolean rule.
const PG_BOOLEAN_LITERALS: ReadonlySet<string> = new Set([
  'true', 'false', 't', 'f', 'yes', 'no', 'y', 'n', 'on', 'off', '1', '0',
]);

/**
 * True when `literalText` is a single-quoted string PostgreSQL would accept as
 * a boolean. `'yes'`, `'t'`, `' TRUE '`, `'0'` → true; `'abc'`, `''` → false.
 * Non-string input → false.
 */
export function quotedStringIsBoolean(literalText: string): boolean {
  const inner = stripStringLiteralQuotes(literalText);
  if (inner === null) return false;
  return PG_BOOLEAN_LITERALS.has(inner.trim().toLowerCase());
}
